/**
 * Toast deduplication constants and utilities
 *
 * These constants ensure consistent deduplication behavior across member
 * (Redux) and premember (Context) toast implementations.
 */

/**
 * Time window for deduplication (5 seconds) Matches toast display duration to
 * prevent duplicates while toast is visible
 */
export const TOAST_DEDUPLICATION_WINDOW_MS = 5000

/**
 * Time window for cleanup of old deduplication entries (10 seconds) Entries
 * older than this are eligible for removal
 */
export const TOAST_CLEANUP_WINDOW_MS = 10000

/**
 * Quick bailout threshold for cleanup (10 entries) Maps/objects with this many
 * or fewer entries skip cleanup iteration for performance. For larger maps, we
 * always clean up to prevent memory leaks in long-running components.
 */
export const TOAST_CLEANUP_QUICK_BAILOUT = 10

/**
 * Creates a unique key for toast deduplication
 *
 * @param content - Toast message content
 * @param sentiment - Toast sentiment/severity
 * @returns Unique key for this toast combination
 */
export function createToastKey(content: string, sentiment: string): string {
  return `${content}-${sentiment}`
}

/**
 * Checks if a toast should be deduplicated based on recent history
 *
 * @param recentToasts - Map/object of recent toast timestamps
 * @param toastKey - Unique key for the toast
 * @param currentTime - Current timestamp (defaults to Date.now())
 * @returns True if toast should be skipped (is a duplicate)
 */
export function shouldDeduplicateToast(
  recentToasts: Record<string, number> | Map<string, number>,
  toastKey: string,
  currentTime: number = Date.now(),
): boolean {
  const lastShown =
    recentToasts instanceof Map
      ? recentToasts.get(toastKey)
      : recentToasts[toastKey]

  if (!lastShown) {
    return false
  }

  return currentTime - lastShown < TOAST_DEDUPLICATION_WINDOW_MS
}

/**
 * Cleans up old entries from toast deduplication tracking
 *
 * This is a pure function that returns a new Map with old entries removed.
 * Performance optimized with hybrid approach:
 *
 * - Quick bailout for small maps (≤10 entries) - returns original map
 * - Always cleans up larger maps to prevent memory leaks in long-running
 *   components (e.g., map components open for hours)
 *
 * @param recentToasts - Map of recent toast timestamps
 * @param currentTime - Current timestamp (defaults to Date.now())
 * @returns New Map with old entries removed, or original Map if small enough
 */
export function cleanupOldToastEntries(
  recentToasts: Map<string, number>,
  currentTime: number = Date.now(),
): Map<string, number> {
  // Quick bailout for small maps - no cleanup needed
  if (recentToasts.size <= TOAST_CLEANUP_QUICK_BAILOUT) {
    return recentToasts
  }

  // For maps > 10 entries, always clean up to prevent memory leaks
  const newMap = new Map<string, number>()

  for (const [key, timestamp] of recentToasts.entries()) {
    if (currentTime - timestamp <= TOAST_CLEANUP_WINDOW_MS) {
      newMap.set(key, timestamp)
    }
  }

  return newMap
}

/**
 * Cleans up old entries from Redux-compatible plain object tracking
 *
 * This is a pure function that returns a new object with old entries removed.
 * Performance optimized with hybrid approach:
 *
 * - Quick bailout for small objects (≤10 entries) - returns original object
 * - Always cleans up larger objects to prevent memory leaks in Redux state
 *
 * @param recentToasts - Plain object of recent toast timestamps
 * @param currentTime - Current timestamp (defaults to Date.now())
 * @returns New object with old entries removed, or original object if small
 *   enough
 */
export function cleanupOldToastEntriesObject(
  recentToasts: Record<string, number>,
  currentTime: number = Date.now(),
): Record<string, number> {
  // Quick bailout for small objects - no cleanup needed
  const entryCount = Object.keys(recentToasts).length
  if (entryCount <= TOAST_CLEANUP_QUICK_BAILOUT) {
    return recentToasts
  }

  // For objects > 10 entries, always clean up to prevent memory leaks
  return Object.fromEntries(
    Object.entries(recentToasts).filter(
      ([, timestamp]) => currentTime - timestamp <= TOAST_CLEANUP_WINDOW_MS,
    ),
  )
}
